import React, { useEffect, useState } from 'react'
import { ScrollView, TouchableOpacity, View } from 'react-native'
import { useSelector } from 'react-redux'
import { useIsFocused, useNavigation } from '@react-navigation/native'
import moment from 'moment'
import { WeightLossRecurringCallsBanner } from '@src/components/InformationPopups/WeightLossRecurringCallsBanner'
import { ErrorMessages } from '@src/config'
import { StyleService, useStyleSheet } from '@src/style/service'
import { getCoachDescriptor, Storage, useDispatchAsync, useSnack } from '@src/utils'
import { NotificationBanner } from '@src/components/notifications/NotificationBanner'
import { LiveSurfaceBanner } from '@src/components/notifications/banners/LiveSurfaceBanner'
import { EhrAppointmentStateKind, NotificationEngineScreen, UiInlineAlert } from '@src/types'
import { Feature, InlineAlert, useFeatureFlag } from '@src/components'
import { FULL_DISPLAY_TIME_FORMAT_WITH_COMMA } from '@config/momentFormat.ts'
import { useFeeProduct } from '@screens/NutritionistHub/hooks/useFeeProduct.ts'
import { useAppointmentSoonAlert } from '@src/screens/NutritionistHub/hooks/useAppointmentSoonAlert'
import { MemberTopNotes } from '@src/screens/NutritionistHub/components/MemberNotes/MemberTopNotes'
import { useStorageValue } from '@src/utils/storage'
import { Text } from '@components/base'
import { userSelector } from '@selectors/app.ts'
import { useMissedCallAlert } from '@screens/NutritionistHub/hooks/useMissedCallAlert.ts'
import { useActiveVideoSubscription } from '@src/hooks/useActiveVideoSubscription.ts'
import { useNoMorePaidCallAlert } from '@screens/NutritionistHub/hooks/useNoMorePaidCallsAlert.ts'
import { UhcOnboardingBanner } from '@src/screens/Home/components/Banners/UhcOnboardingBanner'
import { LabPanelsBanner } from '@screens/NutritionistHub/components/LabPanels/LabPanelsBanner.tsx'
import { FirstCallQuestionnaireBanner } from '@src/screens/Home/components/Banners/FirstCallQuestionnaireBanner'
import {
  ehrAppointmentSchedulingInfoSelector,
  insurancePolicySelector,
  latestUserEhrAppointmentSelector,
  pastEhrAppointmentsSelector,
  pendingEhrAppointmentsSelector,
} from '../models/nutritionistHub.selectors'
import { useNoMoreAllotedCallsAlert } from '../hooks'
import { useNutritionistMenuProducts } from '../hooks/useNutritionistMenuProducts'
import { useEhrNutritionistHubModals } from '../hooks/useNutritionistHubModals'
import { useInsuranceCardSubmissionData } from '../hooks/useInsuranceCardSubmissionData'
import { useEhrEarlierSlotAvailableAlert } from '../hooks/useEarlierSlotAvailableAlert'
import { NutritionistMenuSection } from './NutritionistMenu/NutritionistMenuSection'
import { UpcomingAppointments } from './UpcomingCalls'
import { StressManagementBanner } from './StressManagement/StressManagementBanner'
import { NextCallSection } from './NextCall/NextCallSection'
import { CareTeamCard } from './CareTeamCard'

export const EhrNutritionistHubScreen = () => {
  const styles = useStyleSheet(themedStyle)
  const navigation = useNavigation()
  const latestAppointment = useSelector(latestUserEhrAppointmentSelector)
  const appointmentSchedulingInfo = useSelector(ehrAppointmentSchedulingInfoSelector)
  const insurancePolicy = useSelector(insurancePolicySelector)
  const isFocused = useIsFocused()
  const showSnack = useSnack()
  const dispatchAsync = useDispatchAsync()
  const user = useSelector(userSelector)
  const coachDescriptor = getCoachDescriptor(user)
  const { showNoAllotedCallsAlert, onClose: onAlertClose } = useNoMoreAllotedCallsAlert()
  const { showCallSoonAlert, onClose: onShowCallSoonAlertClose } = useAppointmentSoonAlert(
    latestAppointment,
  )
  const pastAppointments = useSelector(pastEhrAppointmentsSelector)
  const latestPastAppointment = pastAppointments[0] as typeof pastAppointments[0] | null
  const { showMissedCallAlert, missedCallAlertProps } = useMissedCallAlert(latestPastAppointment)
  const {
    showAlert: showEarlierSlotAvailableAlert,
    onPress: onEarlierSlotAvailableAlertPress,
  } = useEhrEarlierSlotAvailableAlert()
  const noShowFeeProduct = useFeeProduct(latestAppointment?.noShowFeeProduct)
  const insuranceCardSubmissionData = useInsuranceCardSubmissionData(insurancePolicy)

  const videoCallCreditsEnabled = useFeatureFlag(Feature.VideoCallCredits)
  const memberNotesFeatureEnabled = useFeatureFlag(Feature.MemberNotes)
  const insuranceCardSubmissionEnabled = useFeatureFlag(Feature.InsuranceCardSubmission)
  const earlierAvailableSlotsCheckFeatureEnabled = useFeatureFlag(
    Feature.VideoCallsEarlierAvailableSlotsCheck,
  )
  const serviceCentricAppointmentSchedulerEnabled = useFeatureFlag(
    Feature.ServiceCentricAppointmentScheduler,
  )
  const [shouldShowDot, setShouldShowDot] = useStorageValue(Storage.NUTRITIONIST_HUB_RED_DOT)

  const [loading, setLoading] = useState(true)

  const nutritionistMenuProducts = useNutritionistMenuProducts()

  const onSubmitInsuranceCardAlertClose = () => {
    insurancePolicy && insurancePolicy.lastState && navigation.navigate('SubmitInsuranceCardScreen')
  }

  const showSubmitInsuranceCardAlert = insuranceCardSubmissionData.isInsuranceCardSubmissionPending

  const nutritionistSwitchEnabled = useFeatureFlag(Feature.RdsSwitchWhenRdIsLeaving)

  const { handleModals: handleHubModals } = useEhrNutritionistHubModals()

  const pendingAppointments = useSelector(pendingEhrAppointmentsSelector)
  const { videoCallSubscription, videoCallSubscriptionProduct } = useActiveVideoSubscription()
  const { showNoMorePaidCallAlert, noMorePaidCallAlertProps } = useNoMorePaidCallAlert(
    videoCallSubscription,
    videoCallSubscriptionProduct,
  )

  const showHitYourGoalAlert =
    videoCallCreditsEnabled &&
    pendingAppointments.length > 0 &&
    latestAppointment?.lastState.kind === EhrAppointmentStateKind.PendingSchedule &&
    (!insurancePolicy?.eligible || !user?.onboardingExperience?.participating)

  const cardsLoading = !appointmentSchedulingInfo
  const latestAppointmentAttendances = latestAppointment?.attendances ?? []
  const attendance = latestAppointmentAttendances.find(
    (attendance) => attendance.user.id === user?.id,
  )

  const showCallSoonNoShowFeeAlert =
    showCallSoonAlert && noShowFeeProduct && latestAppointment && attendance?.imposesLateChangeFees
  const showCallSoonNoShowCreditAlert =
    showCallSoonAlert && latestAppointment && !noShowFeeProduct && videoCallCreditsEnabled

  useEffect(() => {
    if (!shouldShowDot || !isFocused) {
      return
    }

    setShouldShowDot(false)
  }, [isFocused, setShouldShowDot, shouldShowDot])

  useEffect(() => {
    if (!isFocused) {
      return
    }

    let aborted = false

    const asyncEffect = async () => {
      try {
        const promises = [
          dispatchAsync({ type: 'nutritionistHub/fetchAppointmentInfo' }),
          dispatchAsync({ type: 'nutritionistHub/fetchAppointmentSchedulingInfo' }),
          dispatchAsync({ type: 'nutritionistHub/fetchInsurancePolicy' }),
          dispatchAsync({ type: 'users/fetch' }),
          dispatchAsync({ type: 'nutritionistHub/fetchPendingEhrAppointments' }),
          dispatchAsync({ type: 'nutritionistHub/fetchPastEhrAppointments' }),
          dispatchAsync({ type: 'nutritionistHub/fetchCoachAssignments' }),
        ]

        if (videoCallCreditsEnabled) {
          promises.push(dispatchAsync({ type: 'nutritionistHub/fetchCoachingCallAllowance' }))
        }

        if (latestAppointment?.id && earlierAvailableSlotsCheckFeatureEnabled) {
          promises.push(
            dispatchAsync({
              type: 'nutritionistHub/fetchEhrEarlierAvailableSlots',
              payload: { appointmentId: latestAppointment.id },
            }),
          )
        }

        if (nutritionistSwitchEnabled) {
          promises.push(
            dispatchAsync({
              type: 'nutritionistHub/fetchAllEhrAppointments',
            }),
          )
        }

        await Promise.all(promises)
      } catch (error: any) {
        const errorMessage = error?.message || ErrorMessages.ServerError
        showSnack(errorMessage, null, 'error')
      } finally {
        if (!aborted) {
          setLoading(false)
        }
      }
    }
    asyncEffect()

    return () => {
      aborted = true
    }
  }, [
    dispatchAsync,
    showSnack,
    isFocused,
    latestAppointment?.id,
    earlierAvailableSlotsCheckFeatureEnabled,
    nutritionistSwitchEnabled,
    videoCallCreditsEnabled,
  ])

  useEffect(() => {
    if (!isFocused) {
      return
    }

    if (loading) {
      return
    }

    handleHubModals()
  }, [loading, isFocused, handleHubModals])

  const onSchedulePress = () => {
    if (!latestAppointment) {
      return
    }

    navigation.navigate('CallScheduling', {
      appointmentKind: latestAppointment.kind,
      pendingAppointmentId: latestAppointment.id,
      allowBackNavigation: true,
    })
  }

  return (
    <ScrollView>
      <LiveSurfaceBanner screen={NotificationEngineScreen.NutritionistHub} />
      <NotificationBanner screen={NotificationEngineScreen.NutritionistHub} />
      <View style={styles.container}>
        <View style={styles.videoCallContainer}>
          {insuranceCardSubmissionEnabled && showSubmitInsuranceCardAlert && (
            <TouchableOpacity
              onPress={onSubmitInsuranceCardAlertClose}
              accessibilityLabel="Submit insurance card"
            >
              <InlineAlert
                style={styles.alert}
                message="Your consult is on hold. We need your insurance card to re-run the benefits check."
                onClose={onSubmitInsuranceCardAlertClose}
                closeIcon="caret-right"
              />
            </TouchableOpacity>
          )}
          {showNoAllotedCallsAlert && (
            <InlineAlert
              style={styles.alert}
              message={`You have exhausted your primary coverage for insurance-covered video calls for this year.
                However, you may be eligible for additional coverage.
                Please ask your ${coachDescriptor} if you are interested in applying.`}
              onClose={onAlertClose}
            />
          )}
          {showCallSoonNoShowFeeAlert && (
            <InlineAlert
              category={UiInlineAlert.Info}
              style={styles.alert}
              message={`Keep your goals in sight! Join your call at ${moment(
                latestAppointment.meetingAt,
              ).format(FULL_DISPLAY_TIME_FORMAT_WITH_COMMA)} to steer clear of a $${
                noShowFeeProduct.price
              } fee. Your ${coachDescriptor} will wait for 10 minutes before marking you as a no show.`}
              onClose={onShowCallSoonAlertClose}
            />
          )}
          {showCallSoonNoShowCreditAlert && (
            <InlineAlert
              category={UiInlineAlert.Info}
              style={styles.alert}
              message={`Stay on track! Join your call at ${moment(
                latestAppointment.meetingAt,
              ).format(FULL_DISPLAY_TIME_FORMAT_WITH_COMMA)} to avoid losing a call credit.`}
              onClose={onShowCallSoonAlertClose}
            />
          )}
          {showMissedCallAlert && !showCallSoonAlert && missedCallAlertProps && (
            <InlineAlert
              style={styles.alert}
              category={missedCallAlertProps.alertCategory}
              message={missedCallAlertProps.message}
              closeIcon={missedCallAlertProps.rightIconName}
              onClose={missedCallAlertProps.onRightIconPress}
              leftIconName={missedCallAlertProps.leftIconName}
              onLeftIconClick={missedCallAlertProps.onLeftIconPress}
              leftIconAccessibilityLabel={missedCallAlertProps.leftIconAccessibilityLabel}
            />
          )}
          {showNoMorePaidCallAlert && !showMissedCallAlert && noMorePaidCallAlertProps && (
            <InlineAlert
              style={styles.alert}
              category={noMorePaidCallAlertProps.alertCategory}
              message={
                <Text type="regular">
                  <Text type="regular" bold>
                    You're out of calls!{'\n'}
                  </Text>
                  <Text type="regular">{noMorePaidCallAlertProps.message}</Text>
                </Text>
              }
              closeIcon={noMorePaidCallAlertProps.rightIconName}
              onClose={noMorePaidCallAlertProps.onRightIconPress}
              leftIconName={noMorePaidCallAlertProps.leftIconName}
              onLeftIconClick={noMorePaidCallAlertProps.onLeftIconPress}
              leftIconAccessibilityLabel={noMorePaidCallAlertProps.leftIconAccessibilityLabel}
            />
          )}
          <CareTeamCard />
          <View style={styles.banners}>
            <UhcOnboardingBanner />
            <LabPanelsBanner />
            <StressManagementBanner skipLoadingSchedulingInfo />
            <WeightLossRecurringCallsBanner />
            <FirstCallQuestionnaireBanner />
          </View>
          {showEarlierSlotAvailableAlert && (
            <TouchableOpacity
              onPress={onEarlierSlotAvailableAlertPress}
              accessibilityLabel="Reschedule your call"
            >
              <InlineAlert
                category={UiInlineAlert.Info}
                style={styles.alert}
                // eslint-disable-next-line max-len
                message={`An earlier slot with your ${coachDescriptor} is now available. Reschedule your call to kickstart your health journey sooner.`}
                onClose={onEarlierSlotAvailableAlertPress}
                closeIcon="caret-right"
              />
            </TouchableOpacity>
          )}
          <NextCallSection appointment={latestAppointment} loading={cardsLoading} />

          {showHitYourGoalAlert && (
            <TouchableOpacity
              onPress={onSchedulePress}
              accessibilityLabel="Hit your goal - schedule your next call"
            >
              <InlineAlert
                style={styles.hitYourGoalAlert}
                iconName="bell-ringing"
                message={
                  <Text bold type="regular">
                    Hit your goal!
                  </Text>
                }
                secondaryMessage={`Schedule your next call with your ${coachDescriptor} and start seeing results`}
                iconStyle={{ width: 32, height: 32 }}
                category={UiInlineAlert.Warning}
                closeIcon="caret-right"
                onClose={onSchedulePress}
              />
            </TouchableOpacity>
          )}
        </View>
        {latestAppointment && (
          <>
            <View style={styles.divider} />
            <UpcomingAppointments
              hideScheduleNextCall={serviceCentricAppointmentSchedulerEnabled}
            />
          </>
        )}
        {memberNotesFeatureEnabled && (
          <>
            <View style={styles.divider} />
            <MemberTopNotes />
          </>
        )}
        {nutritionistMenuProducts.length > 0 && (
          <>
            <View style={styles.divider} />
            <NutritionistMenuSection
              style={styles.nutritionistMenuContainer}
              products={nutritionistMenuProducts}
            />
          </>
        )}
      </View>
    </ScrollView>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    marginHorizontal: 16,
    marginTop: 8,
  },
  videoCallContainer: { marginBottom: 16 },
  divider: {
    height: 8,
    backgroundColor: 'theme.surface.base2',
    marginHorizontal: -16,
  },
  banners: {
    gap: 8,
    marginBottom: 16,
  },
  cardContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    minHeight: 200,
  },
  alert: {
    marginBottom: 16,
  },
  nutritionistMenuContainer: {
    marginVertical: 16,
  },
  hitYourGoalAlert: {
    marginTop: 16,
  },
})
